import { useEffect } from 'react'
import { useBlogCms } from '@/hooks/useBlogCms'
import { useRouter } from 'next/router'
import { format, parseISO } from 'date-fns'
import { BLOG_CONTENT_API, SITE_URL } from '@/constants'
import { replaceString } from '@/utils/replaceString'
import type { NextPage, GetServerSidePropsContext } from 'next'
import {
  Box,
  Flex,
  HStack,
  Image,
  Progress,
  Text,
  VStack,
  useStyleConfig,
  useColorModeValue,
} from '@chakra-ui/react'
import { ContainerBase } from '@/components/containers/ContainerBase'
import { useTranslations } from 'next-intl'
import { RelatedArticles } from '@/features/education/related-articles'
import { AuthorInfo } from '@/features/education/author-info'
import NextLink from 'next/link'
import { ResearchArticle } from '@/features/education/research-article'
import { Share } from '@/features/education/share'
import { Follow } from '@/features/education/follow'

const ArticleDetailPage: NextPage = () => {
  const styles = useStyleConfig('Box', { variant: 'white' })
  const t = useTranslations()
  const { compassContent, loading, getContent } = useBlogCms()
  const router = useRouter()

  const defaultBgColor = useColorModeValue('white', 'black')
  const secondaryTextColor = useColorModeValue('black', 'gray.840')

  useEffect(() => {
    if (!router?.query?.slug) return

    const abortController = new AbortController()

    const fetchArticle = async () => {
      await getContent(
        {
          path: `/content/posts/slug/${router?.query?.slug}`,
          params: '&include=tags,authors',
        },
        abortController
      )

      if (compassContent?.posts?.length === 0) {
        await getContent(
          {
            path: `/content/pages/slug/${router?.query?.slug}`,
            params: '&include=tags,authors',
          },
          abortController
        )
      }
    }

    fetchArticle()

    return () => {
      abortController.abort()
    }
  }, [router])

  const isPage =
    !compassContent?.posts?.length && compassContent?.pages?.length > 0

  let article = null
  if (compassContent?.posts?.length > 0) {
    article = compassContent.posts[0]
  } else if (compassContent?.pages?.length > 0) {
    article = compassContent.pages[0]
  }

  const isResearch = article?.custom_template === 'custom-mining-index'
  const displayShareButtons = !article?.tags?.some(
    (tag) => tag.slug === 'hash-no-share-buttons'
  )
  const displayFollowButtons = !article?.tags?.some(
    (tag) => tag.slug === 'hash-no-follow-buttons'
  )
  const displayDisclaimer = !article?.tags?.some(
    (tag) => tag.slug === 'hash-no-disclaimer'
  )

  if (isResearch) {
    return <ResearchArticle article={article} loading={loading} />
  }

  return (
    <>
      <Box __css={styles}>
        {loading && <Progress size="xs" isIndeterminate colorScheme="brand" />}
        {article && (
          <ContainerBase>
            <VStack width="100%" alignItems="flex-start">
              <Box className={'article'}>
                <Box className="post-full post">
                  <Box as="header" className="post-full-header">
                    {!isPage && (
                      <Box
                        as="section"
                        className="post-full-tags"
                        css={{
                          a: {
                            color: 'var(--text-secondary)',
                          },
                        }}
                      >
                        {article?.primary_tag?.name && (
                          <NextLink
                            href={`/education/tag/${article?.primary_tag?.slug}`}
                          >
                            {article?.primary_tag?.name}
                          </NextLink>
                        )}
                      </Box>
                    )}
                    <Box as="h1" className="post-full-title">
                      {article?.title}
                    </Box>
                    <Box as="p" className="post-full-custom-excerpt">
                      {article?.custom_excerpt}
                    </Box>
                    {!isPage && (
                      <>
                        <HStack
                          marginTop="50px !important"
                          alignItems="center"
                          spacing="15px"
                        >
                          <Box
                            as="ul"
                            display="flex"
                            flexWrap="wrap"
                            padding="0 !important"
                            margin="0 !important"
                            css={{
                              listStyle: 'none !important',
                            }}
                          >
                            {article?.authors?.map((author: any, i: number) => (
                              <Box
                                key={`author-image-${i}`}
                                as="li"
                                cursor="pointer"
                                margin="0 -4px !important"
                                background="white"
                                padding="2px !important"
                                borderRadius="full"
                              >
                                <AuthorInfo author={author} />
                              </Box>
                            ))}
                          </Box>
                          <VStack alignItems="flex-start" spacing="0">
                            <Flex
                              margin="0"
                              fontSize="13px !important"
                              textTransform="uppercase"
                              css={{
                                a: { color: 'var(--text-secondary)' },
                              }}
                            >
                              {article?.authors?.map((author: any) => (
                                <Box
                                  key={author?.slug}
                                  _after={{
                                    content: "','",
                                  }}
                                  css={{
                                    '&:last-child:after': {
                                      content: "''",
                                    },
                                  }}
                                >
                                  <NextLink
                                    href={`/education/author/${author?.slug}`}
                                  >
                                    {author?.name}
                                  </NextLink>
                                </Box>
                              ))}
                            </Flex>
                            <HStack fontSize="12px !important" color="#939699">
                              <Box>
                                {`${t('common.published')}:
                            ${format(
                              parseISO(article?.published_at),
                              'MMM dd yyyy'
                            )}, ${t('common.last-updated')}:
                            ${format(
                              parseISO(article?.updated_at),
                              'MMM dd yyyy'
                            )}`}
                              </Box>
                              <Box>•</Box>
                              <Box>{article?.reading_time} MIN READ</Box>
                            </HStack>
                          </VStack>
                        </HStack>
                        {article?.feature_image && (
                          <Box className="post-full-image">
                            <Image
                              src={article.feature_image}
                              alt={article.title}
                              className="kg-image"
                            />
                          </Box>
                        )}
                        {displayFollowButtons && (
                          <Box mt="6 !important">
                            <Follow />
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                  {article?.access && (
                    <Box className="post-full-content">
                      <Box
                        className="post-content"
                        dangerouslySetInnerHTML={{
                          __html: article?.html,
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </VStack>
          </ContainerBase>
        )}
        {displayShareButtons && (
          <Share url={router.asPath} justifyContent="center" />
        )}
        {article?.primary_tag?.slug ? (
          <RelatedArticles
            id={article?.id}
            current_slug={article?.slug}
            primary_tag={article?.primary_tag?.slug}
          />
        ) : (
          <Box py={10} />
        )}
        {article && displayDisclaimer && (
          <Box py={8} bg={defaultBgColor}>
            <ContainerBase>
              <Text
                color={secondaryTextColor}
                lineHeight={1.5}
                fontSize="10px"
                fontStyle="italic"
              >
                DISCLAIMER: This content is provided for educational and
                informational purposes only and is not intended to serve as
                financial, investment, legal, tax, accounting, or operational
                advice. You acknowledge and agree that the making available of
                this content to you by Compass Mining shall not constitute the
                provision of investment service or any other professional
                service or general service by Compass Mining. Bitcoin mining
                involves market, regulatory, technical, and operational risks,
                and outcomes may vary significantly based on factors beyond any
                party’s control. Past performance, estimates, or historical data
                do not guarantee future results. Prior to engaging in Bitcoin
                mining, reader should conduct its own independent investigation,
                review and analysis of the Bitcoin mining industry, and Reader
                hereby acknowledges that Compass Mining has not made any express
                or implied representations or warranties, as to the accuracy and
                completeness of any information provided herein, upon which
                Reader can rely. Compass Mining disclaims any liability for
                losses, damages, or adverse outcomes arising from the use of
                this content. Readers assume full responsibility for their
                decisions and should conduct independent research and consult
                qualified professional advisors before taking action.
              </Text>
            </ContainerBase>
          </Box>
        )}
      </Box>
    </>
  )
}

export async function getServerSideProps({
  locale,
  params,
}: GetServerSidePropsContext) {
  let article: any = {}
  let hasNoRobotTag = false
  try {
    const resource = (path: string) => {
      return replaceString(BLOG_CONTENT_API, {
        key: 'resource',
        value: path,
      })
    }

    const qparams = '&include=tags,authors'

    const postPath = `/content/posts/slug/${params?.slug}`
    const postRes = await fetch(`${resource(postPath)}${qparams}`, {})
    const postContent = await postRes.json()
    article = postContent?.posts?.length > 0 ? postContent?.posts[0] : null

    if (!article) {
      const pagePath = `/content/pages/slug/${params?.slug}`
      const pageRes = await fetch(`${resource(pagePath)}${qparams}`, {})
      const pageContent = await pageRes.json()
      article = pageContent?.pages?.length > 0 ? pageContent?.pages[0] : null

      if (!article && !pageRes.ok) {
        if (pageRes.status === 404) {
          return { notFound: true }
        }
      }
    } else if (!postRes.ok) {
      if (postRes.status === 404) {
        return { notFound: true }
      }
    }

    hasNoRobotTag = article?.tags?.some((tag) => tag.slug === 'hash-norobot')
  } catch (e) {
    console.error(JSON.stringify(e))
  }

  return {
    props: {
      messages: (await import(`../../../locales/${locale}.json`)).default,
      revalidate: 60,
      htmlClass: 'education',
      seo: {
        title: article?.title || '',
        description: article?.custom_excerpt || '',
        image: article?.feature_image || '',
        url: `${SITE_URL}/education/${params?.slug}`,
        shouldIndex: !hasNoRobotTag,
        breadcrumbs: [
          {
            position: 2,
            item: `${SITE_URL}/education`,
            name: 'Compass Mining Content',
          },
          {
            position: 3,
            item: `${SITE_URL}/education/${params?.slug}`,
            name: article?.title || '',
          },
        ],
      },
    },
  }
}

export default ArticleDetailPage
